import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Lock, Unlock, Save, Calendar, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { db, dbHelpers } from "@/lib/database";
import { vault } from "@/lib/vault";

const EMOJIS = ['😊', '😢', '😡', '😴', '😍', '🤔', '😎', '🥳', '😌', '🦄', '🌈', '✨', '💖', '🌟', '💫', '🎀'];

const MOOD_COLORS = {
  happy: 'from-yellow-100 to-orange-100',
  sad: 'from-blue-100 to-indigo-100',
  angry: 'from-red-100 to-pink-100',
  tired: 'from-purple-100 to-gray-100',
  love: 'from-pink-100 to-rose-100',
  neutral: 'from-green-100 to-teal-100'
};

const DiaryPage = () => {
  const [isLocked, setIsLocked] = useState(true);
  const [password, setPassword] = useState([]);
  const [entryText, setEntryText] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [vaultExists, setVaultExists] = useState(false);
  const [mood, setMood] = useState('neutral');
  const [isRecording, setIsRecording] = useState(false);
  const [showEraseConfirm, setShowEraseConfirm] = useState(false);

  useEffect(() => {
    // Check if vault exists
    const checkVault = async () => {
      const exists = await vault.vaultExists(db);
      setVaultExists(exists);
    };
    checkVault();
  }, []);

  // Load diary entry when date changes or vault unlocks
  useEffect(() => {
    if (isLocked) return;

    const loadEntry = async () => {
      try {
        const entry = await dbHelpers.getDiaryByDate(selectedDate);
        if (entry && entry.ciphertext) {
          const decrypted = await vault.decryptEntry(entry.ciphertext, entry.iv);
          setEntryText(decrypted);
          setMood(entry.mood || 'neutral');
        } else {
          setEntryText('');
          setMood('neutral');
        }
      } catch (error) {
        console.error('Failed to load entry:', error);
        setEntryText('');
        setMood('neutral');
      }
    };

    loadEntry();
  }, [selectedDate, isLocked]);

  const handleEmojiSelect = (emoji) => {
    if (password.length < 3) {
      setPassword([...password, emoji]);
    }
  };

  const handleUnlock = async () => {
    const passwordStr = password.join('');

    if (password.length !== 3) {
      toast.error('Please select 3 emojis');
      return;
    }

    try {
      if (!vaultExists) {
        // First time - initialize vault
        await vault.initializeVault(passwordStr, db);
        setVaultExists(true);
        setIsLocked(false);
        toast.success('🔐 Vault created! Remember your emojis - NO RECOVERY possible!');
      } else {
        // Unlock existing vault
        await vault.unlockVault(passwordStr, db);
        setIsLocked(false);
        toast.success('✨ Diary unlocked!');
      }
      setPassword([]);
    } catch (error) {
      toast.error(error.message || '❌ Wrong password! Try again.');
      setPassword([]);
    }
  };

  const handleClearPassword = () => {
    setPassword([]);
  };

  const handleSave = async () => {
    if (!entryText.trim()) {
      toast.error('Please write something first!');
      return;
    }

    try {
      // Encrypt entry
      const encrypted = await vault.encryptEntry(entryText);

      // Save to IndexedDB
      await dbHelpers.saveDiaryEntry({
        date: selectedDate,
        ciphertext: encrypted.ciphertext,
        iv: encrypted.iv,
        mood: mood,
        created_at: new Date().toISOString()
      });

      toast.success('💫 Diary entry saved!');
    } catch (error) {
      console.error('Failed to save entry:', error);
      toast.error('Failed to save entry');
    }
  };

  const handleEraseVault = async () => {
    if (!showEraseConfirm) {
      setShowEraseConfirm(true);
      toast.warning('⚠️ Click again to confirm: This will PERMANENTLY delete ALL diary entries!');
      setTimeout(() => setShowEraseConfirm(false), 5000);
      return;
    }

    try {
      await vault.eraseVault(db);
      setVaultExists(false);
      setIsLocked(true);
      setEntryText('');
      setPassword([]);
      toast.success('🧹 Vault erased. All diary entries deleted.');
    } catch (error) {
      console.error('Failed to erase vault:', error);
      toast.error('Failed to erase vault');
    }
  };

  const handleExportVault = async () => {
    try {
      await vault.exportVault(db);
      toast.success('📥 Backup exported! Save this file safely.');
    } catch (error) {
      console.error('Failed to export vault:', error);
      toast.error('Failed to export backup');
    }
  };

  const handleImportVault = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      await vault.importVault(file, db);
      setVaultExists(true);
      setIsLocked(true);
      setPassword([]);
      toast.success('📤 Backup imported! Unlock with your password.');
      event.target.value = ''; // Reset file input
    } catch (error) {
      console.error('Failed to import vault:', error);
      toast.error('Failed to import backup: ' + error.message);
    }
  };

  const handleVoiceInput = async () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error('Voice input not supported in this browser');
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();

    recognition.lang = 'en-US';
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onstart = () => {
      setIsRecording(true);
      toast.info('🎤 Listening...');
    };

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setEntryText(prev => prev + ' ' + transcript);
      toast.success('✅ Added to diary!');
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      toast.error('Voice input failed. Please try again.');
      setIsRecording(false);
    };

    recognition.onend = () => {
      setIsRecording(false);
    };

    recognition.start();
  };

  if (isLocked) {
    return (
      <div className="min-h-screen px-6 py-8 md:px-12 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full bg-white border-2 border-border rounded-3xl shadow-hard-xl p-8"
        >
          <div className="text-center mb-8">
            <Lock className="w-16 h-16 text-magic mx-auto mb-4" />
            <h1 className="font-fredoka text-4xl font-bold text-foreground mb-2">
              Lock & Key Diary 🔐
            </h1>
            <p className="font-nunito text-muted-foreground">
              {vaultExists ? 'Enter your 3-emoji password' : 'Create a 3-emoji password - NO RECOVERY!'}
            </p>
          </div>

          {/* Password Display */}
          <div className="flex justify-center gap-4 mb-8">
            {[0, 1, 2].map((index) => (
              <div
                key={index}
                data-testid={`password-slot-${index}`}
                className="w-16 h-16 border-2 border-border rounded-xl bg-muted flex items-center justify-center text-3xl"
              >
                {password[index] || '?'}
              </div>
            ))}
          </div>

          {/* Emoji Selector */}
          <div className="grid grid-cols-8 gap-2 mb-6">
            {EMOJIS.map((emoji) => (
              <button
                key={emoji}
                data-testid={`emoji-${emoji}`}
                onClick={() => handleEmojiSelect(emoji)}
                disabled={password.length >= 3}
                className="w-10 h-10 text-2xl hover:scale-110 transition-transform disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {emoji}
              </button>
            ))}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              data-testid="clear-password-button"
              onClick={handleClearPassword}
              className="flex-1 px-6 py-3 border-2 border-border rounded-full font-fredoka font-semibold text-foreground hover:bg-muted transition-colors"
            >
              Clear
            </button>
            <button
              data-testid="unlock-button"
              onClick={handleUnlock}
              disabled={password.length !== 3}
              className="flex-1 px-6 py-3 bg-magic text-white rounded-full font-fredoka font-semibold shadow-hard hover:shadow-hard-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {vaultExists ? 'Unlock' : 'Create Vault'}
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen px-6 py-8 md:px-12 bg-gradient-to-br ${MOOD_COLORS[mood]} transition-colors duration-500`}>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-4xl mx-auto"
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="font-fredoka text-4xl md:text-5xl font-bold text-foreground">
            My Secret Diary 📖
          </h1>
          <button
            data-testid="lock-diary-button"
            onClick={() => setIsLocked(true)}
            className="p-3 bg-white border-2 border-border rounded-full shadow-hard hover:shadow-hard-lg transition-all"
          >
            <Unlock className="w-6 h-6 text-magic" />
          </button>
        </div>

        {/* Date Selector */}
        <div className="mb-6">
          <label className="font-nunito text-sm font-semibold text-foreground mb-2 block">
            Select Date
          </label>
          <div className="relative">
            <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <input
              type="date"
              data-testid="date-input"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="w-full pl-12 pr-4 py-3 bg-white border-2 border-border rounded-xl font-nunito text-foreground shadow-inner focus:border-magic focus:ring-4 focus:ring-magic/20 outline-none"
            />
          </div>
        </div>

        {/* Mood Selector */}
        <div className="mb-6">
          <label className="font-nunito text-sm font-semibold text-foreground mb-3 block">
            How are you feeling?
          </label>
          <div className="flex gap-3 flex-wrap">
            {[
              { mood: 'happy', emoji: '😊', label: 'Happy' },
              { mood: 'sad', emoji: '😢', label: 'Sad' },
              { mood: 'love', emoji: '😍', label: 'Loved' },
              { mood: 'tired', emoji: '😴', label: 'Tired' },
              { mood: 'angry', emoji: '😡', label: 'Angry' },
              { mood: 'neutral', emoji: '😌', label: 'Calm' }
            ].map((item) => (
              <button
                key={item.mood}
                data-testid={`mood-${item.mood}`}
                onClick={() => setMood(item.mood)}
                className={`px-4 py-2 rounded-full font-nunito font-semibold transition-all ${mood === item.mood
                  ? 'bg-white text-foreground border-2 border-magic shadow-hard'
                  : 'bg-white/50 text-muted-foreground border-2 border-transparent hover:bg-white'
                  }`}
              >
                {item.emoji} {item.label}
              </button>
            ))}
          </div>
        </div>

        {/* Diary Entry */}
        <div className="bg-white border-2 border-border rounded-3xl shadow-hard-xl p-6 mb-6">
          <textarea
            data-testid="diary-textarea"
            value={entryText}
            onChange={(e) => setEntryText(e.target.value)}
            placeholder="Dear Diary... Write your thoughts here ✨"
            className="w-full h-64 p-4 bg-transparent font-patrick text-xl text-foreground placeholder:text-muted-foreground resize-none focus:outline-none"
          />
        </div>

        {/* Action Buttons */}
        <div className="flex gap-4">
          <button
            data-testid="voice-input-button"
            onClick={handleVoiceInput}
            disabled={isRecording}
            className="flex items-center gap-2 px-6 py-3 bg-accent text-white rounded-full font-fredoka font-semibold shadow-hard hover:shadow-hard-lg transition-all disabled:opacity-50"
          >
            <Sparkles className="w-5 h-5" />
            {isRecording ? 'Listening...' : 'Voice Input'}
          </button>
          <button
            data-testid="save-diary-button"
            onClick={handleSave}
            className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white rounded-full font-fredoka font-semibold shadow-hard hover:shadow-hard-lg transition-all"
          >
            <Save className="w-5 h-5" />
            Save Entry
          </button>
        </div>

        {/* Vault Management */}
        <div className="mt-6 pt-6 border-t-2 border-border">
          <h3 className="font-fredoka text-lg font-semibold text-foreground mb-3">Vault Management</h3>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleExportVault}
              className="flex items-center gap-2 px-4 py-2 bg-accent/20 text-accent border border-accent rounded-lg font-nunito font-semibold hover:bg-accent/30 transition-all"
            >
              📥 Export Backup
            </button>
            <label className="flex items-center gap-2 px-4 py-2 bg-secondary/20 text-secondary border border-secondary rounded-lg font-nunito font-semibold hover:bg-secondary/30 transition-all cursor-pointer">
              📤 Import Backup
              <input
                type="file"
                accept=".json"
                onChange={handleImportVault}
                className="hidden"
              />
            </label>
            <button
              onClick={handleEraseVault}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg font-nunito font-semibold transition-all ${showEraseConfirm
                ? 'bg-red-500 text-white border-red-600'
                : 'bg-red-50 text-red-600 border-red-300 hover:bg-red-100'
                }`}
            >
              🧹 {showEraseConfirm ? 'Confirm Erase?' : 'Erase Vault'}
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Export your encrypted backup regularly. Erase will permanently delete ALL diary entries.
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default DiaryPage;
